// Automatiseringernes motor i drift (edge-funktionen "automation", _shared/automation.ts) – samme regler som testversionen:
// betingelser og målgruppe (opfyldt), tidspunkt (koertid), stilletid for SMS (stilleTimer) og fletning af felter (variabler/udfyld).
// Alt her er rene funktioner, så de kan testes uden database og bruges både i browseren (Opsætning › Automatiseringer) og på serveren.
//  • Hændelser (booking oprettet, godkendt …): haendelseJobs → beskeder nu eller et planlagt job (forskydning/stilletid).
//  • Tid før/efter bookingens start/slut: tidsJobs → ét job pr. (booking, regel, handling) med nøglen "tid:…" – flyttes
//    bookingen, flytter jobbet med; aflyste bookinger og tider, der er passeret, får intet.
//  • Mails til kunden med login-knappen: teksten har LINK_PLADSHOLDER; send-mail sætter et nyt engangslink ind, når mailen sendes.
//  • Markedsføring (samtykke.ts) sendes kun til kunder med samtykke og får afmeldingslinket i bunden.
//  • Anmodning om anmeldelse (anmeldelse.ts): ikke til afmeldte kunder, højst én pr. kunde pr. 90 dage, afmeldingslink i bunden.
//  • Kundens mail med "Vedhæft kalender" og mailen om en aflyst booking får en .ics-fil (kalender.ts), som send-mail vedhæfter.
//  • Flere biler i samme bestilling: én samlet bekræftelse (samletKontekst) – se _shared/automation.ts.
import { BEKRAEFTELSER, LOGIN_HAENDELSER, LINK_PLADSHOLDER, KUNDEKONTO_SKABELON, kundeKontoMail, medKontoIntro, medLoginLink } from './kundekonto.ts';
import { STANDARD_WORKFLOWS, koertid, lokalTilMs, maalgruppeTom, medBilListe, opfyldt, stilleTimer, udfyld, variabler, type Haendelse, type Handling, type Kontekst, type Workflow } from './automation.ts';
import { FORMAAL, INGEN_SAMTYKKE, afmeldLink, formaalFor, harAfmeldLink, maaSende, medAfmelding } from './samtykke.ts';
import { DEAKTIVERET } from './deaktiver.ts';
import { GDPR_SPAERRET } from './gdpr.ts';
import { AFMELDT, INDEN_90, INTET_LINK, anmeldLink, inden90 } from './anmeldelse.ts';
import { kalenderForBesked, kalenderMetodeFor } from './kalender.ts';
import { fletIndstillinger, standardIndstillinger, type Indstillinger } from './indstillinger.ts';
import { email as tjekEmail, rens, rensTekst, telefon as tjekTelefon } from './validering.ts';
import { datoTekst, klokken, tidsrum } from './dato.ts';
import { betalingFor, fakturagebyrTekst, kvitteringLinjer } from './betaling.ts';
import { gebyrerFra } from './gebyrer.ts';
import { momsIInkl } from './moms.ts';
import { ApiFejl } from './fejl.ts';

/** Så længe efter tidspunktet må en påmindelse stadig sendes (fx hvis serveren har været nede). */
export const GRACE_TID = 2 * 3600_000;
/** Hændelser og planlagte job, der er ældre end dette, sendes ikke (gamle beskeder skader mere, end de gavner). */
export const GRACE_HAENDELSE = 2 * 86_400_000;
/** Bookingstatusser, der kan få påmindelser (tjekket igen mod workflowets betingelser, når de sendes). */
const AKTIVE = ['afventer', 'godkendt', 'i_gang', 'faerdig'];
export const HAENDELSE_TYPER: Haendelse[] = ['booking_oprettet', 'booking_godkendt', 'booking_flyttet', 'booking_aflyst', 'booking_faerdig', 'betaling_modtaget', 'kundesag_oprettet'];

export type AutoBrand = { id: string; navn: string; aktiv: boolean; domaene: string; telefon: string;
  cvr?: string; adresse?: string; mobilepay?: string; viva?: string; telefontid?: string };
export type AutoKlient = {
  id: string; slug: string; navn: string; domaene: string; app_url: string; spaerret: boolean;
  indst: Indstillinger; workflows: Workflow[]; brands: AutoBrand[];
};
/** En booking med alt, automatiseringerne skal bruge (automation_raekker i databasen). */
export type AutoRaekke = {
  id: number; kode: string; status: string; ydelse_id: string; ydelse: string; tilvalg_ids: string[]; tilvalg: string;
  kilde: string; partner: string; brand_id: string | null; medlem: boolean; ny_kunde: boolean; betalt: boolean; faktura: boolean;
  oensket: { dato: string; start: number } | null; dato: string | null; start: number | null; slut: number | null; oprettet: string;
  kunde_id: string; kunde_navn: string; kunde_email: string; kunde_telefon: string; kunde_konto: boolean; medarbejder: boolean;
  adresse: string; postnr: string; by: string; bil: string; nummerplade: string; total: number;
  steamer: string; steamer_telefon: string; steamer_email: string;
  ordre_id: number | null; samtykke: boolean; afmeld_token: string;
  // Kvitteringen: bookingens tal (øre ekskl. moms), tilvalg og gebyrer – og Viva- og MobilePay-linket pr. booking, når serveren har lavet dem
  pris?: number; moms?: number; rabat?: number; tilvalg_linjer?: { navn: string; antal: number; pris: number }[]; gebyrer?: { navn: string; beloeb: number }[];
  viva_link?: string | null;
  mobilepay_link?: string | null;
  // Anmeldelser (anmeld_raekker i databasen – lagt på af _shared/automation.ts)
  afmeldt?: boolean; anmeld_token?: string; anmodet?: string | null;
  deaktiveret?: boolean;   // kundens profil er deaktiveret (anmeld_raekker)
  gdpr_spaerret?: boolean; // kunden er spærret efter ønske om sletning (anmeld_raekker)
};
export type AutoBesked = { kanal: 'mail' | 'sms'; til: string; emne: string; tekst: string; skabelon: string; kundelogin: boolean; fejl?: string; ics?: string };
export type Job = { w: Workflow; h: Handling; tid: number };

/** Klientens række fra databasen → klar til motoren. Har klienten ingen egne automatiseringer, gælder standarden. */
export function autoKlient(r: Omit<AutoKlient, 'indst' | 'workflows'> & { indst: unknown; workflows: Workflow[] | null }): AutoKlient {
  return { ...r, indst: fletIndstillinger(r.indst, standardIndstillinger({ navn: r.navn })), workflows: r.workflows?.length ? r.workflows : STANDARD_WORKFLOWS };
}

/** Adressen, kunden logger ind på: brandets domæne, virksomhedens eget domæne eller Freemis adresse + slug. */
export function kundeAdresseFor(kl: { slug: string; domaene: string; app_url: string }, brand?: { aktiv: boolean; domaene: string } | null): string {
  if (brand?.aktiv && brand.domaene) return `https://${brand.domaene}/`;
  if (kl.domaene) return `https://${kl.domaene}/`;
  return `${(kl.app_url || 'https://freemi.dk').replace(/\/$/, '')}/${kl.slug}/`;
}

const aktivtBrand = (kl: AutoKlient, id: string | null) => (id ? kl.brands.find((b) => b.id === id && b.aktiv) ?? null : null);

export function kontekstFra(r: AutoRaekke, kl: AutoKlient): Kontekst {
  const br = aktivtBrand(kl, r.brand_id);
  const adr = kundeAdresseFor(kl, br);
  const bet = betalingFor(kl.indst.betaling, br);
  return {
    booking_id: r.id, kode: r.kode, status: r.status, ydelse_id: r.ydelse_id, ydelse: r.ydelse, tilvalg_ids: r.tilvalg_ids, tilvalg: r.tilvalg,
    kilde: r.kilde, partner: r.partner, brand: r.brand_id ?? '', medlem: r.medlem, ny_kunde: r.ny_kunde, betalt: r.betalt, faktura: r.faktura,
    tid_aendret: !!r.oensket && r.start != null && (r.oensket.dato !== r.dato || r.oensket.start !== r.start),
    oensket: r.oensket ? `${datoTekst(r.oensket.dato)} kl. ${klokken(r.oensket.start)}` : '',
    dato: r.dato ?? '', start: r.start, slut: r.slut,
    kunde_navn: r.kunde_navn, kunde_email: r.kunde_email, kunde_telefon: r.kunde_telefon,
    adresse: r.adresse, postnr: r.postnr, by: r.by, bil: r.bil, nummerplade: r.nummerplade, total: r.total, moms: r.moms ?? momsIInkl(r.total, kl.indst.moms.procent), flyt_timer: gebyrerFra(kl.indst.gebyrer).flytTimer,
    steamer: r.steamer, steamer_telefon: r.steamer_telefon,
    firma: br?.navn ?? (kl.indst.firma.navn || kl.navn), firma_telefon: br?.telefon || kl.indst.firma.telefon,
    links: { mine: `${adr}#/mine`, betal: `${adr}#/mine`, trustpilot: kl.indst.trustpilot.url, kundecenter: `${adr}#/kundecenter`, medlemskab: `${adr}#/medlem`, book: `${adr}#/book`,
      afmeld: r.afmeld_token ? afmeldLink(adr, r.afmeld_token) : '' },
    samtykke: r.samtykke, afmeldt: !!r.afmeldt, deaktiveret: !!r.deaktiveret, gdpr_spaerret: !!r.gdpr_spaerret,
    ...(r.anmeld_token ? { anmeld: { link: anmeldLink(adr, r.anmeld_token), anmodet: r.anmodet ?? null } } : {}),
    firma_adresse: br?.adresse || kl.indst.firma.adresse,
    betaling: { mobilepay: bet.mobilepay, mobilepay_link: r.mobilepay_link || '', viva: r.viva_link || bet.vivaLink, fakturagebyr: fakturagebyrTekst(gebyrerFra(kl.indst.gebyrer).faktura), telefontid: bet.telefontid },
    kvittering: r.pris == null ? undefined : {
      booking_nr: String(r.id), dato: r.dato ?? '', adresse: [r.adresse, [r.postnr, r.by].filter(Boolean).join(' ')].filter(Boolean).join(', '), bil: r.bil, nummerplade: r.nummerplade,
      steamer: (r.steamer || '').split(' ')[0], linjer: kvitteringLinjer({ ydelse: r.ydelse, pris: r.pris, rabat: r.rabat ?? 0, tilvalg: r.tilvalg_linjer ?? [], gebyrer: r.gebyrer ?? [] }),
      rabat: r.rabat ?? 0, pris: r.pris, moms: r.moms ?? r.total - r.pris, total: r.total, momsProcent: kl.indst.moms.procent,
      firma: { navn: br?.navn ?? (kl.indst.firma.navn || kl.navn), cvr: br?.cvr || kl.indst.firma.cvr, adresse: br?.adresse || kl.indst.firma.adresse },
    },
  };
}

/** Kan kunden få login-knappen? Kundelogin slået til, en gyldig e-mail, e-mailen er ikke en medarbejders, og profilen er aktiv. */
export const loginMulig = (r: AutoRaekke, kl: AutoKlient) => kl.indst.kunder.login && !!tjekEmail(r.kunde_email) && !r.medarbejder && !r.deaktiveret && !r.gdpr_spaerret;

/** Hvorfor handlingen ikke skal sendes nu (null = send): betingelserne, en spærret kunde (GDPR) eller en kunde med deaktiveret profil (intet til kunden),
 *  markedsføring uden kundens samtykke, eller en anmodning om anmeldelse til en afmeldt kunde / en kunde, der har fået én
 *  inden for 90 dage. */
export function hindring(w: Workflow, h: Handling, k: Kontekst, igen = false, nu = Date.now()): string | null {
  if (!opfyldt(w, k)) return igen ? 'Betingelserne passer ikke længere (fx betalt eller ændret)' : 'Betingelserne passer ikke';
  if (h.til === 'kunde' && k.gdpr_spaerret) return GDPR_SPAERRET;
  if (h.til === 'kunde' && k.deaktiveret) return DEAKTIVERET;
  if (h.til === 'kunde' && formaalFor(w) === 'anmeldelse') {
    if (k.afmeldt) return AFMELDT;
    if (!k.anmeld?.link) return INTET_LINK;
    if (inden90(k.anmeld.anmodet, nu)) return INDEN_90;
  }
  if (!maaSende(w, h, !!k.samtykke, !!k.afmeldt)) return INGEN_SAMTYKKE;
  return null;
}

/** Beskeden, en handling giver for en booking (samme fletning som testversionen). nyKonto: første booking – bekræftelsen fortæller om kontoen. */
export function byggBesked(w: Workflow, h: Handling, k: Kontekst, o: { login: boolean; nyKonto?: boolean; steamer_email: string; firma_email: string; klient: string; nu?: number }): AutoBesked {
  const bekraeft = BEKRAEFTELSER.includes(w.haendelse);
  const kundeMail = h.type === 'mail' && h.til === 'kunde';
  const altid = LOGIN_HAENDELSER.includes(w.haendelse) && !!k.booking_id;
  const login = o.login && kundeMail && (altid || /\{\{\s*link_mine\s*\}\}/.test(h.tekst));
  const v = variabler(login ? { ...k, links: { ...k.links, mine: LINK_PLADSHOLDER } } : k);
  let tekst = udfyld(h.tekst, v);
  if (kundeMail) tekst = medBilListe(tekst, k);
  if (login && altid) tekst = medLoginLink(tekst, LINK_PLADSHOLDER);
  if (login && bekraeft && o.nyKonto) tekst = medKontoIntro(tekst, LINK_PLADSHOLDER, k.firma);
  if (h.til === 'kunde' && harAfmeldLink(w)) tekst = medAfmelding(tekst, h.type, k.links.afmeld ?? '', k.firma, formaalFor(w));
  const raa = h.til === 'kunde' ? (h.type === 'mail' ? k.kunde_email : k.kunde_telefon)
    : h.til === 'steamer' ? (h.type === 'mail' ? o.steamer_email : k.steamer_telefon)
    : (h.type === 'mail' ? o.firma_email : '');
  const til = h.type === 'mail' ? tjekEmail(raa ?? '') : tjekTelefon(raa ?? '');
  const b: AutoBesked = { kanal: h.type, til: til ?? (raa || '(mangler)'), emne: h.type === 'mail' ? udfyld(h.emne, v) : '', tekst, skabelon: w.navn, kundelogin: login };
  const metode = kalenderMetodeFor(w, h, k);
  if (metode) b.ics = kalenderForBesked(metode, k, { klient: o.klient, arrangoer: tjekEmail(o.firma_email) ?? 'noreply@freemi.dk', nu: o.nu ?? Date.now() });
  if (!til) b.fejl = h.type === 'mail' ? 'Modtageren har ingen gyldig e-mail' : 'Modtageren har intet gyldigt mobilnummer';
  return b;
}

/** "Din kundekonto er klar" – kun når kunden ikke får en bekræftelse med login-knappen. */
export function kontoBesked(r: AutoRaekke, k: Kontekst): AutoBesked {
  const m = kundeKontoMail({ ny: true, navn: r.kunde_navn, firma: k.firma, link: LINK_PLADSHOLDER,
    booking: { nr: r.id, ydelse: r.ydelse, dato: r.dato ? datoTekst(r.dato) : 'dato aftales', tid: r.start != null ? tidsrum(r.start, r.slut) : 'tid aftales', adresse: `${r.adresse}, ${r.postnr} ${r.by}` } });
  return { kanal: 'mail', til: tjekEmail(r.kunde_email) ?? r.kunde_email, emne: m.emne, tekst: m.tekst, skabelon: KUNDEKONTO_SKABELON, kundelogin: true };
}

const stille = (kl: AutoKlient): [number, number] => [kl.indst.beskeder.stilleFra, kl.indst.beskeder.stilleTil];
const effektiv = (h: Handling, t: number, kl: AutoKlient) => (h.type === 'sms' ? stilleTimer(t, ...stille(kl)) : t);

/** Job for en hændelse. kundeKanaler: kontorets valg ved ny booking (også en kanal, der ellers er slået fra; [] = ingen). */
export function haendelseJobs(kl: AutoKlient, type: Haendelse, k: Kontekst, tidMs: number, kundeKanaler?: ('sms' | 'mail')[]): Job[] {
  const ud: Job[] = [];
  for (const w of [...kl.workflows].sort((a, b) => a.sortering - b.sortering)) {
    if (!w.aktiv || w.haendelse !== type) continue;
    const t = koertid(w, k, tidMs);
    if (t == null) continue;
    for (const h of w.handlinger) if (kundeKanaler && h.til === 'kunde' ? kundeKanaler.includes(h.type) : h.aktiv) ud.push({ w, h, tid: effektiv(h, t, kl) });
  }
  return ud;
}

export const tidsbaseret = (w: Workflow) => w.haendelse === 'booking_start' || w.haendelse === 'booking_slut';
export const tidsNoegle = (booking_id: number, w: Workflow, h: Handling) => `tid:${booking_id}:${w.id}:${h.id}`;

/** Hvornår en tidsbaseret handling skal sendes for bookingen nu – eller null (ingen tid, aflyst, oprettet efter tidspunktet,
 *  SMS'en ville efter stilletiden lande efter starten, eller tiden er passeret for længe siden). */
export function tidsTid(w: Workflow, h: Handling, k: Kontekst, kl: AutoKlient, o: { nu: number; oprettet: number }): number | null {
  if (k.start == null || !k.dato || !AKTIVE.includes(k.status)) return null;
  const t = koertid(w, k, o.nu);
  if (t == null) return null;
  const eff = effektiv(h, t, kl);
  const start = lokalTilMs(k.dato, k.start);
  if (t <= start && eff >= start) return null;
  if (eff < o.oprettet || eff < o.nu - GRACE_TID) return null;
  return eff;
}

/** Alle tidsbaserede job for en booking (aktive regler og handlinger). */
export function tidsJobs(kl: AutoKlient, k: Kontekst, o: { nu: number; oprettet: number }): (Job & { noegle: string })[] {
  const ud: (Job & { noegle: string })[] = [];
  for (const w of kl.workflows) {
    if (!w.aktiv || !tidsbaseret(w)) continue;
    for (const h of w.handlinger) {
      if (!h.aktiv) continue;
      const tid = tidsTid(w, h, k, kl, o);
      if (tid != null) ud.push({ w, h, tid, noegle: tidsNoegle(k.booking_id, w, h) });
    }
  }
  return ud;
}

/** Hvilke bookingdatoer skal tjekkes for tidsbaserede regler (dage før og efter i dag)? null = ingen tidsbaserede regler. */
export function tidsVindue(kl: AutoKlient): { foer: number; efter: number } | null {
  let foer = 1, efter = 1, nogen = false;
  for (const w of kl.workflows) {
    if (!w.aktiv || !tidsbaseret(w) || !w.handlinger.some((h) => h.aktiv)) continue;
    nogen = true;
    const f = w.forskydning;
    const dage = f ? Math.ceil((f.antal * { minutter: 1, timer: 60, dage: 1440 }[f.enhed]) / 1440) + 1 : 1;
    if (!f || f.retning === 'foer') efter = Math.max(efter, dage); else foer = Math.max(foer, dage);
  }
  return nogen ? { foer, efter } : null;
}

/** Tjek og rens en automatisering, før den gemmes (testversionen og drift). */
export function tjekWorkflow(w: Workflow, nytId: () => string): Workflow {
  const navn = rens(w.navn, 120);
  if (navn.length < 2) throw new ApiFejl('Skriv et navn.', { navn: 'Skriv et navn.' });
  if (!w.handlinger.length) throw new ApiFejl('Tilføj mindst én handling (mail eller SMS).');
  for (const h of w.handlinger) {
    if (h.type === 'mail' && rens(h.emne, 200).length < 2) throw new ApiFejl('Hver mail skal have et emne.');
    if (rensTekst(h.tekst, 10000).length < 2) throw new ApiFejl('Hver handling skal have en tekst.');
  }
  if (w.handlinger.length > 10) throw new ApiFejl('Højst 10 handlinger i én automatisering.');
  const m = w.maalgruppe;
  if (m && !['alle', 'kun', 'undtagen'].includes(m.tilstand)) throw new ApiFejl('Vælg hvem der får beskeden.');
  if (m && m.tilstand !== 'alle' && maalgruppeTom(m)) throw new ApiFejl(m.tilstand === 'kun' ? 'Vælg mindst én gruppe under "Kun".' : 'Vælg mindst én gruppe, der ikke skal have beskeden.');
  const maalgruppe = m ? { ...m, partnere: [...new Set(m.partnere.map((p) => rens(p, 60)).filter(Boolean))] } : undefined;
  if (w.formaal && !FORMAAL.some(([f]) => f === w.formaal)) throw new ApiFejl('Vælg om det er service, markedsføring eller anmeldelse.');
  return { ...w, formaal: formaalFor(w), maalgruppe, navn, id: w.id || nytId() };
}
